import React from "react";
import {
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";

interface RolePermissionFormProps {
  validation: any;
  roles?: any[];
  permissions?: any[];
  rolePermissions?: any[];
}

const RolePermissionForm: React.FC<RolePermissionFormProps> = ({
  validation,
  roles = [],
  permissions = [],
  rolePermissions = [],
}) => {
  const activeRoles = React.useMemo(() => {
    if (!Array.isArray(roles)) return [];
    return roles.filter((role: any) => 
      role.status?.toLowerCase() === "active"
    );
  }, [roles]);

  const activePermissions = React.useMemo(() => {
    if (!Array.isArray(permissions)) return [];
    return permissions.filter((permission: any) => 
      permission.status?.toLowerCase() === "active"
    );
  }, [permissions]);

  const roleOptions = activeRoles.map((role) => ({
    label: role.rolename,
    value: role.roleid,
  }));

  const availablePermissions = React.useMemo(() => {
    const selectedRoleId = Number(validation.values.roleid);
    if (!selectedRoleId) return activePermissions;
    const selectedRole = activeRoles.find((role: any) => role.roleid === selectedRoleId);
    if (!selectedRole) return activePermissions;

    const assignedPermissionNames = rolePermissions
      .filter((rp: any) => {
        if (rp.roleid !== undefined) {
          return rp.roleid === selectedRoleId;
        }
        return rp.rolename?.toLowerCase() === selectedRole.rolename?.toLowerCase();
      })
      .map((rp: any) => rp.permissionname?.toLowerCase());
    return activePermissions.filter((permission: any) => {
      const permissionName = permission.permissionname?.toLowerCase();
      return !assignedPermissionNames.includes(permissionName);
    });
  }, [activePermissions, activeRoles, rolePermissions, validation.values.roleid]);

  const permissionOptions = availablePermissions.map((permission) => ({
    label: permission.permissionname,
    value: permission.permissionid,
  }));

  React.useEffect(() => {
    const selectedPermissionId = Number(validation.values.permissionid);
    if (selectedPermissionId && !availablePermissions.some((p: any) => p.permissionid === selectedPermissionId)) {
      validation.setFieldValue("permissionid", "");
      validation.setFieldTouched("permissionid", false);
    }
  }, [availablePermissions, validation]);

  return (
    <Row className="g-3">
      <Col md={12}>
        <Label className="fw-bold mb-1">
          Role <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={roleOptions}
            name="roleid"
            placeholder="Select Role"
            value={roleOptions.find((opt) => opt.value === Number(validation.values.roleid)) || null}
            onChange={(e: { value: any }) => {
              validation.setFieldValue("roleid", e?.value);
              validation.setFieldValue("permissionid", "");
              validation.setFieldTouched("permissionid", false);
            }}
            onBlur={() => validation.setFieldTouched("roleid", true)}
            classNamePrefix="react-select"
            className={validation.touched.roleid && validation.errors.roleid ? "react-select-error" : ""}
          />
        </div>
        {validation.touched.roleid && validation.errors.roleid && (
          <div className="invalid-feedback d-block">{validation.errors.roleid}</div>
        )}
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Permission <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={permissionOptions}
            name="permissionid"
            placeholder="Select Permission"
            value={permissionOptions.find((opt) => opt.value === Number(validation.values.permissionid)) || null}
            onChange={(e: { value: any }) => validation.setFieldValue("permissionid", e?.value)}
            onBlur={() => validation.setFieldTouched("permissionid", true)}
            classNamePrefix="react-select"
            className={validation.touched.permissionid && validation.errors.permissionid ? "react-select-error" : ""}
          />
        </div>
        {validation.touched.permissionid && validation.errors.permissionid && (
          <div className="invalid-feedback d-block">{validation.errors.permissionid}</div>
        )}
      </Col>
    </Row>
  );
};

export default RolePermissionForm;

